import React from 'react';
import { connect } from 'react-redux';
import { cn } from 'lib/utils';
import FieldRow from './FieldRow';
import { getIssuesComponent, getErrorIssues, getWarningIssues, getInfoIssues } from '../../../../../../../utils/validationSummary';

/**
 * Modern (Tailwind) counterpart of components/PropertyPicker.
 *
 * Same name, same props, same outward handleChange(propertyName, value) contract, so
 * migrating a screen is an import-path change and nothing else.
 *
 * Everything above render() is copied VERBATIM from the original: getMin/getMax's
 * valueMeta.off handling, the tempValue/value/error state machine, the Mirek rounding
 * in handleMouseUp, the savedValue restoration in componentDidUpdate and the
 * validity/title messages all decide what value reaches the driver. Only render()
 * differs. Deliberately still a class — see the note in modern/PropertyDropdown.jsx.
 */
class PropertyPicker extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            value: this.props.property.value,
            tempValue: this.props.property.value,
            error: +this.props.property.value < this.getMin(this.props.property.value) || +this.props.property.value > this.getMax(this.props.property.value)
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);

        this.getMin = this.getMin.bind(this);
        this.getMax = this.getMax.bind(this);
    }

    getMin(value) {
        return ('' + value) !== this.props.property.valueMeta.off ? +this.props.min : Math.min(this.props.min, this.props.property.valueMeta.off);
    }

    getMax(value) {
        return ('' + value) !== this.props.property.valueMeta.off ? +this.props.max : Math.max(this.props.max, this.props.property.valueMeta.off);
    }

    handleChange(e) {
        const value = +e.target.value;

        this.setState({
            tempValue: '' + value,
        });
    }

    handleKeyUp(e) {
        if (e.target.value === null || e.target.value === undefined || e.target.value === '' || (('' + this.state.value) === ('' + this.state.tempValue))) {
            return;
        }
        const value = this.state.tempValue;
        this.setState({
            value: '' + value,
            error: value < this.getMin(value) || +value > this.getMax(value)
        });

        //Add it to have a generic message in english. The error text, in case the value is out of range, is autogenerated in the language of the pc
        if (!e.target.validity.valid) {
            e.target.title = value < this.getMin(value) ? `Select a value greater than ${this.getMin(value)}` : `Select a value lower than ${this.getMax(value)}`;
        }
        else {
            e.target.title = '';
        }

        this.props.handleChange(this.props.property.info.name, value);
    }

    handleMouseUp(e) {
        if (e.target.value === null || e.target.value === undefined || e.target.value === '' || (('' + this.state.value) === ('' + this.state.tempValue))) {
            return;
        }

        let value = this.state.tempValue;

        //Mirek conversion
        if (this.props.type === "Mirek") {
            value = (+this.state.tempValue) > (+this.state.value)
                ? Math.round(1000000 / (Math.round(1000000 / (+this.state.tempValue)) - 1))
                : Math.round(1000000 / (Math.round(1000000 / (+this.state.tempValue)) + 1));
        }

        this.setState({
            value: '' + value,
            tempValue: '' + value,
            error: value < this.getMin(value) || +value > this.getMax(value)
        });

        //Add it to have a generic message in english. The error text, in case the value is out of range, is autogenerated in the language of the pc
        if (!e.target.validity.valid) {
            e.target.title = value < this.getMin(value) ? `Select a value greater than ${this.getMin(value)}` : `Select a value lower than ${this.getMax(value)}`;
        }
        else {
            e.target.title = '';
        }

        this.props.handleChange(this.props.property.info.name, value);
    }

    componentDidUpdate(prevProps, prevState) {
        if (!(isNaN(this.props.property.value) && isNaN(prevProps.property.value))) {
            if (this.props.property.value !== prevProps.property.value) {
                this.setState({ value: this.props.property.value });
            }

            //needed when handleChange is triggered from other component, the props are updated but not the state
            if (this.props.property.value !== prevState.value) {
                this.setState({ value: this.props.property.value });
                this.setState({ tempValue: this.props.property.value });
            }

            if (this.props.property.value !== prevProps.property.value
                || this.props.min !== prevProps.min
                || this.props.max !== prevProps.max) {
                this.setState({ error: +this.props.property.value < this.getMin(this.props.property.value) || +this.props.property.value > this.getMax(this.props.property.value) });
            }

            if (this.props.issues !== prevProps.issues) {
                const infos = getInfoIssues(this.props.issues, this.props.property);
                if (infos.length > 0) {
                    for (var i = 0; i < infos.length; i++) {
                        if (infos[i].savedValue != null) {
                            this.setState({
                                value: infos[i].savedValue,
                                tempValue: infos[i].savedValue,
                            });
                        }
                    }
                }
            }
        }
    }

    render() {
        const errors = getErrorIssues(this.props.issues, this.props.property);
        const warnings = getWarningIssues(this.props.issues, this.props.property);

        // `.danger` / `.warning` are standalone 2px border rules in stylesheet.css and
        // still apply on top of the Tailwind border below.
        const state = errors.length > 0 ? 'danger' : warnings.length > 0 ? 'warning' : '';

        return (
            <FieldRow label={this.props.title} unit={this.props.measureUnit} disabled={this.props.disabled} className={this.props.className}>
                <input
                    disabled={this.props.disabled}
                    className={cn(
                        'h-8 w-20 rounded-md border border-input bg-background px-2 text-center text-sm font-semibold',
                        'text-[var(--brand-color1)] shadow-sm outline-none',
                        'focus-visible:ring-1 focus-visible:ring-ring',
                        'disabled:cursor-not-allowed disabled:text-muted-foreground',
                        state
                    )}
                    style={this.props.inputStyleInner}
                    name={this.props.property.info.name}
                    type="number"
                    min={this.props.min}
                    max={this.props.max}
                    step={this.props.step}
                    value={Number(this.state.value || this.props.property.value) || 0}
                    onChange={this.handleChange}
                    onKeyUp={this.handleKeyUp}
                    onMouseUp={this.handleMouseUp} />

                {getIssuesComponent(errors, warnings)}
            </FieldRow>
        );
    };
}

const mapStateToProps = (state, ownProps) => {
    return {
        validationSummary: state.project.validationSummary,
        issues: state.project.issueByProperty,
    };
};

const mapDispatchToProps = {
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PropertyPicker);
